import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { FaArrowLeft, FaTimes } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { selectTheme } from "../../../Store/features/design/designSlice";
import { createCampaign, getTemplateById, getFilteredInvestorsListById, getFilteredInvestorsList, getTemplates, getCampaignById, updateCampaign, scheduleCampaign, runCampaign } from "../../../Service/user";
import "./CreateCampaign.scss";
import { FiArrowLeft, FiCheck, FiClock, FiPlay } from "react-icons/fi";
import Modal from "react-modal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SubscriptionPopup from "../../PopUp/SubscriptionPopUp/SubcriptionPop";


const CreateCampaign = () => {
	const theme = useSelector(selectTheme);
	const navigate = useNavigate();
	const location = useLocation();
	const { campaignId } = useParams();
	const { selectedLists, selectedTemplate } = location.state || {};
	const loggedInUserEmail = JSON.parse(localStorage.getItem('loggedInUser'))?.email || '';
	const user = JSON.parse(localStorage.getItem('loggedInUser'));
	const [subscriptionPopup, setSubscriptionPopup] = useState(false);
	const [campaignData, setCampaignData] = useState({});	
	const [listsInfo, setListsInfo] = useState([]);
	const [templateInfo, setTemplateInfo] = useState(null);
	const [totalInvestors, setTotalInvestors] = useState(0);
	const [totalVcs, setTotalVcs] = useState(0);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [createdCampaignId, setCreatedCampaignId] = useState(null);
	const [showScheduleModal, setShowScheduleModal] = useState(false);
	const [scheduledDate, setScheduledDate] = useState(new Date());
	const [scheduledTime, setScheduledTime] = useState(new Date());
	const [isScheduling, setIsScheduling] = useState(false);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		setCampaignData({
			campaignName: "",
			investorLists: selectedLists || [],
			template: selectedTemplate || null,
			emailSubject: "",
			emailBody: "",
			scheduledAt: null,
			cc: loggedInUserEmail ? [loggedInUserEmail] : [],
			bcc: []
		})
	}, []);
	
	useEffect(() => {
		const init = async () => {
			setLoading(true);
			setError(null);
			
			if (!selectedLists || !selectedTemplate) {
				setError("Missing required information");
				toast.error("Missing required information");
				navigate("/campaign");
				return;
			}
			
			try {
				await Promise.all([
					fetchListsInfo(),
					fetchTemplateInfo()
				]);
			} catch (err) {
				console.error("Error initializing campaign:", err);
			} finally {
				setLoading(false);
			}
		};
		
		init();
	}, [selectedLists, selectedTemplate]);
	
	const fetchListsInfo = async () => {
		try {
			const listsData = [];
			const uniqueInvestorIds = new Set();
			const vcEmails = new Set();
			let totalVcs = 0;
			
			for (const listId of selectedLists) {
				const response = await getFilteredInvestorsListById(listId);
				if (response.status === 200) {
					listsData.push(response.list);
					response.list.investors.forEach(investor => {
						uniqueInvestorIds.add(investor._id || investor.id);
					});

					
					
					// Extract VC emails from the list
					if (response.list.vcs && response.list.vcs.length > 0) {
						response.list.vcs.forEach(vc => {
							totalVcs++;
							if (vc.people && Array.isArray(vc.people)) {
								vc.people.forEach(person => {
									if (person.c_email) {
										vcEmails.add(person.c_email);
									}
								});
							}
						});
					}
				}
			}
			
			setListsInfo(listsData);
			setTotalInvestors(uniqueInvestorIds.size);
			setTotalVcs(totalVcs);
			// Add VC emails to CC list
			if (vcEmails.size > 0) {
				setCampaignData(prev => ({
					...prev,
					cc: [...new Set([...prev.cc, ...Array.from(vcEmails)])]
				}));
			}
		} catch (error) {
			console.error("Error fetching lists info:", error);
			toast.error("Failed to fetch lists information");
			throw error;
		}
	};
	
	const fetchTemplateInfo = async () => {
		try {
			const response = await getTemplateById(selectedTemplate);
			if (response.status === 200 && response.template) {
				setTemplateInfo(response.template);
				setCampaignData(prev => ({
					...prev,
					emailSubject: response.template.emailSubject || "",
					emailBody: response.template.emailBody || ""
				}));
			} else {
				setError("Failed to fetch template information");
				toast.error("Failed to fetch template information");
				throw new Error("Template not found");
			}
		} catch (error) {
			console.error("Error fetching template info:", error);
			setError("Failed to fetch template information");
			toast.error("Failed to fetch template information");
			throw error;
		}
	};

	const handleGoBack = () => {
		navigate("/campaign");
	};
	
	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setCampaignData(prev => ({
			...prev,
			[name]: value
		}));
	};
	
	const handleEditorChange = (content) => {
		setCampaignData(prev => ({
			...prev,
			emailBody: content
		}));
	};

	const handleEmailKeyDown = (e, type) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			const email = e.target.value.trim();
			
			// Basic email validation
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(email)) {
				toast.error('Please enter a valid email address');
				return;
			}
			
			// Check if email already exists in the list
			if (campaignData[type].includes(email)) {
				toast.error('This email is already added');
				e.target.value = '';
				return;
			}
			
			setCampaignData(prev => ({
				...prev,
				[type]: [...prev[type], email]
			}));
			e.target.value = '';
		}
	};

	const handleRemoveEmail = (type, index) => {
		setCampaignData(prev => ({
			...prev,
			[type]: prev[type].filter((_, i) => i !== index)
		}));
	};

	const handleCreateCampaign = async () => {
		if (!campaignData.campaignName) {
			toast.error("Please enter a campaign name");
			return;
		}
		
		try {
			setSaving(true);
			const response = await createCampaign({
				campaignName: campaignData.campaignName,
				investorLists: selectedLists,
				template: selectedTemplate,
				emailSubject: campaignData.emailSubject,
				emailBody: campaignData.emailBody,
				campaignStatus: 'draft',
				cc: campaignData.cc,
				bcc: campaignData.bcc
			});
			
			if (response.status === 201) {
				setCreatedCampaignId(response.campaign._id);
				setShowSuccessModal(true);
			} else {
				toast.error(response.message || "Failed to create campaign");
			}
		} catch (error) {
			console.error("Error creating campaign:", error);
			toast.error("An error occurred while creating the campaign");
		} finally {
			setSaving(false);
		}
	};

	const handleSchedule = async () => {
		if (!campaignData.campaignName) {
			toast.error("Please enter a campaign name");
			return;
		}
		
		try {
			setSaving(true);
			const response = await createCampaign({
				campaignName: campaignData.campaignName,
				investorLists: selectedLists,
				template: selectedTemplate,
				emailSubject: campaignData.emailSubject,
				emailBody: campaignData.emailBody,
				campaignStatus: 'draft',
				cc: campaignData.cc,
				bcc: campaignData.bcc
			});
			
			if (response.status === 201) {
				// Get current date and time
				const now = new Date();
				// Add 5 minutes to current time
				now.setMinutes(now.getMinutes() + 5);
				
				const scheduleResponse = await scheduleCampaign(response.campaign._id, now.toISOString());
				
				if (scheduleResponse.status === 200) {
					toast.success("Campaign scheduled successfully");
					navigate("/campaign");
				} else {
					toast.error(scheduleResponse.message || "Failed to schedule campaign");
				}
			} else {
				toast.error(response.message || "Failed to create campaign");
			}
		} catch (error) {
			console.error("Error scheduling campaign:", error);
			toast.error("An error occurred while scheduling the campaign");
		} finally {
			setSaving(false);
		}
	};

	const handleSendNow = async () => {
		if (!campaignData.campaignName) {
			toast.error("Please enter a campaign name");
			return;
		}
		
		try {
			setSaving(true);
			const now = new Date();
			
			const response = await createCampaign({
				campaignName: campaignData.campaignName,
				investorLists: selectedLists,
				template: selectedTemplate,
				emailSubject: campaignData.emailSubject,
				emailBody: campaignData.emailBody,
				campaignStatus: 'sending',
				sentAt: now.toISOString(),
				cc: campaignData.cc,
				bcc: campaignData.bcc
			});
			
			if (response.status === 201) {
				toast.success("Campaign sent successfully");
				navigate("/campaign");
			} else {
				toast.error(response.message || "Failed to send campaign");
			}
		} catch (error) {
			console.error("Error sending campaign:", error);
			toast.error("An error occurred while sending the campaign");
		} finally {
			setSaving(false);
		}
	};
	
	const handleScheduleCreatedCampaign = () => {
		if(user.subscriptionType !== "Pro"){
			toast.error("Please subscribe to Pro plan to create a campaign");
			setSubscriptionPopup(true);
			return;
		}
		// Set default time to 30 minutes from now
		const defaultTime = new Date();
		defaultTime.setMinutes(defaultTime.getMinutes() + 30);
		
		setScheduledDate(defaultTime);
		setScheduledTime(defaultTime);
		setShowScheduleModal(true);
	};
	
	const handleRunCreatedCampaign = async () => {
		if(user.subscriptionType !== "Pro"){
			toast.error("Please subscribe to Pro plan to create a campaign");
			setSubscriptionPopup(true);
			return;
		}
		try {
			const response = await runCampaign(createdCampaignId, campaignData.cc, campaignData.bcc);
			
			if (response.status === 200) {
				toast.success("Campaign started successfully");
				setShowSuccessModal(false);
				navigate("/campaign");
			} else {
				toast.error(response.message || "Failed to start campaign");
			}
		} catch (error) {
			console.error("Error running campaign:", error);
			toast.error("An error occurred while starting the campaign");
		}
	};

	const handleConfirmSchedule = async () => {
		try {
			setIsScheduling(true);
			
			// Combine date and time
			const scheduledDateTime = new Date(scheduledDate);
			scheduledDateTime.setHours(
				scheduledTime.getHours(),
				scheduledTime.getMinutes(),
				0
			);
			
			// Ensure the scheduled time is in the future
			const now = new Date();
			if (scheduledDateTime <= now) {
				toast.error("Please select a future date and time");
				setIsScheduling(false);
				return;
			}

			const response = await scheduleCampaign(createdCampaignId, scheduledDateTime.toISOString());
			if (response.status === 200) {
				toast.success("Campaign scheduled successfully");
				setShowScheduleModal(false);
				setShowSuccessModal(false);
				navigate("/campaign");
			} else {
				toast.error(response.message || "Failed to schedule campaign");
			}
		} catch (error) {
			console.error("Error scheduling campaign:", error);
			toast.error("An error occurred while scheduling the campaign");
		} finally {
			setIsScheduling(false);
		}
	};

	if (loading) {
		return (
			<div className={`create-campaign ${theme}`}>
				<div className="loading-state">
					<div className="loader"></div>
					<p>Loading campaign information...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className={`create-campaign ${theme}`}>
				<div className="error-state">
					<div className="error-icon">⚠️</div>
					<h3>{error}</h3>
					<button className="back-button" onClick={handleGoBack}>
						Back to Campaign
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className={`create-campaign ${theme}`}>
			<div className="campaign-header">
				<button className="back-button" onClick={handleGoBack}>
					<FaArrowLeft className="back-icon" /> Back to Campaign
				</button>
			</div>

			<div className="campaign-content">
				<div className="campaign-summary">
					<h2>Campaign Summary</h2>
					<div className="summary-details">
						<div className="summary-item">
							<span className="summary-label">Total Lists</span>
							<span className="summary-value">{listsInfo.length}</span>
						</div>
						<div className="summary-item">
							<span className="summary-label">Total Investors</span>
							<span className="summary-value">{totalInvestors}</span>
						</div>
						<div className="summary-item">
							<span className="summary-label">Total VCs</span>
							<span className="summary-value">{totalVcs}</span>
						</div>
						<div className="summary-item">
							<span className="summary-label">Template</span>
							<span className="summary-value">{templateInfo?.templateName}</span>
						</div>
					</div>
				</div>

				<div className="form-section">
					<label className="form-label" htmlFor="campaignName">Campaign Name</label>
					<input
						type="text"
						id="campaignName"
						name="campaignName"
						className="form-input"
						placeholder="Enter campaign name"
						value={campaignData.campaignName}
						onChange={handleInputChange}
					/>
				</div>

				<div className="compose-section">
					<div className="compose-header">
						<h3 className="compose-title">Compose Email</h3>
					</div>

					<div className="subject-section">
						<label className="form-label" htmlFor="emailSubject">Subject</label>
						<input
							type="text"
							id="emailSubject"
							name="emailSubject"
							className="form-input"
							placeholder="Enter email subject"
							value={campaignData.emailSubject}
							onChange={handleInputChange}
						/>
					</div>

					<div className="email-options">
						<div className="cc-section">
							<label className="form-label">CC</label>
							<div className="email-tags-input">
								{campaignData.cc.map((email, index) => (
									<div key={index} className="email-tag">
										<span>{email}</span>
										<button
											type="button"
											className="remove-tag"
											onClick={() => handleRemoveEmail('cc', index)}
										>
											<FaTimes />
										</button>
									</div>
								))}
								<input
									type="email"
									placeholder="Add CC email and press Enter"
									onKeyDown={(e) => handleEmailKeyDown(e, 'cc')}
									className="tag-input"
								/>
							</div>
						</div>

						<div className="bcc-section">
							<label className="form-label">BCC</label>
							<div className="email-tags-input">
								{campaignData.bcc.map((email, index) => (
									<div key={index} className="email-tag">
										<span>{email}</span>
										<button
											type="button"
											className="remove-tag"
											onClick={() => handleRemoveEmail('bcc', index)}
										>
											<FaTimes />
										</button>
									</div>
								))}
								<input
									type="email"
									placeholder="Add BCC email and press Enter"
									onKeyDown={(e) => handleEmailKeyDown(e, 'bcc')}
									className="tag-input"
								/>
							</div>
						</div>
					</div>

					<div className="content-section">
						<label className="form-label">Email Content</label>
						<div className="quill-container">
							<ReactQuill
								theme="snow"
								value={campaignData.emailBody}
								onChange={handleEditorChange}
								placeholder="Compose your email..."
								className="campaign-quill-editor"
							/>
						</div>
					</div>
				</div>
			</div>

			<div className="campaign-footer">
				<button 
					className="footer-button primary" 
					onClick={handleCreateCampaign}
					disabled={saving}
				>
					{saving ? (
						<>
							<span className="button-loader"></span>
							Creating Campaign...
						</>
					) : (
						"Create Campaign"
					)}
				</button>
			</div>
			
			{showSuccessModal && (
				<div className="success-modal-overlay">
					<div className="success-modal">
						<div className="success-icon">
							<FiCheck />
						</div>
						<h3>Campaign Created Successfully!</h3>
						<p>What would you like to do with this campaign?</p>
						
						<div className="success-actions">
							<button 
								className="action-button draft"
								onClick={() => {
									setShowSuccessModal(false);
									navigate("/campaign");
								}}
								disabled={saving}
							>
								<span className="action-icon">
									<FiCheck />
								</span>
								Save as Draft
							</button>
							
							<button 
								className="action-button schedule"
								onClick={handleScheduleCreatedCampaign}
								disabled={saving}
							>
								<span className="action-icon">
									<FiClock />
								</span>
								Schedule Campaign
							</button>
							
							<button 
								className="action-button run"
								onClick={handleRunCreatedCampaign}
								disabled={saving}
							>
								<span className="action-icon">
									<FiPlay />
								</span>
								Run Campaign Now
							</button>
						</div>
					</div>
				</div>
			)}

			{showScheduleModal && (
				<Modal
					isOpen={showScheduleModal}
					onRequestClose={() => setShowScheduleModal(false)}
					contentLabel="Schedule Campaign"
					className="schedule-modal"
					overlayClassName="schedule-modal-overlay"
				>
					<div className="schedule-modal-content">
						<h2>Schedule Campaign</h2>
						<p>Select when you want to send this campaign:</p>
						
						<div className="schedule-form">
							<div className="form-group">
								<label>Date:</label>
								<DatePicker
									selected={scheduledDate}
									onChange={date => setScheduledDate(date)}
									minDate={new Date()}
									dateFormat="MMMM d, yyyy"
									className="date-picker"
								/>
							</div>
							
							<div className="form-group">
								<label>Time:</label>
								<DatePicker
									selected={scheduledTime}
									onChange={time => setScheduledTime(time)}
									showTimeSelect
									showTimeSelectOnly
									timeIntervals={15}
									timeCaption="Time"
									dateFormat="h:mm aa"
									className="time-picker"
								/>
							</div>
						</div>
						
						<div className="schedule-actions">
							<button 
								className="cancel-button" 
								onClick={() => setShowScheduleModal(false)}
								disabled={isScheduling}
							>
								Cancel
							</button>
							<button 
								className="confirm-button" 
								onClick={handleConfirmSchedule}
								disabled={isScheduling}
							>
								{isScheduling ? 'Scheduling...' : 'Schedule Campaign'}
							</button>
						</div>
					</div>
				</Modal>
			)}
			<SubscriptionPopup
				isOpen={subscriptionPopup}
				onClose={() => setSubscriptionPopup(false)}
				fromCampaign={true}
			/>
		</div>
	);
};

export default CreateCampaign;
